import { FC, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOrganization } from 'src/context/OrganizationContext';
import { trpc } from 'src/trpc';
import { MainLayout } from 'src/pages/MainLayout';
import { Loader } from 'src/components/Loader';
import { InvoiceStatusBadge } from 'src/components/invoices/InvoiceStatusBadge';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { Mail, MessageSquare } from 'lucide-react';
import { useToast } from 'src/components/useToast';
import { InvoiceDetailsActionMenu } from 'src/components/invoices/InvoiceDetailsActionMenu';
import { InvoiceStatus } from '@libs/schemas/src/invoice';
import { InvoiceCustomerSection } from 'src/components/invoices/InvoiceCustomerSection';
import { InvoiceItemsSection } from 'src/components/invoices/InvoiceItemsSection';
import { InvoiceBillingAddressSection } from 'src/components/invoices/InvoiceBillingAddressSection';
import { Drawer, DrawerTrigger } from '@/components/ui/drawer';
import {
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerContent,
  DrawerFooter,
  DrawerClose,
} from '@/components/ui/drawer';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ConfirmDialog } from 'src/components/ConfirmDialog';
import { LuHistory } from 'react-icons/lu';
import { InvoiceDetailsSection } from 'src/components/invoices/InvoiceDetailsSection';
import { TotalWithTaxFooter } from 'src/components/TotalWithTaxFooter';
import { formatLongDateTime } from '@libs/services/date';
import { InvoicePaymentLinkSection } from 'src/components/invoices/InvoicePaymentLinkSection';

export const InvoiceDetailsPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { successToast, errorToast } = useToast();
  const [openSendEmailDialog, setOpenSendEmailDialog] = useState(false);
  const [openSendSMSDialog, setOpenSendSMSDialog] = useState(false);
  const [openMarkAsSentDialog, setOpenMarkAsSentDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);

  // Use organization context for org-dependent queries
  const { organizationId, isResolved, isOrganizationSwitching, setIsOrganizationSwitching } =
    useOrganization();

  const {
    data: invoice,
    isLoading,
    isFetching,
    error,
    refetch,
  } = trpc.admin.invoice.adminGetInvoiceById.useQuery(id!, {
    enabled: isResolved && !!organizationId && !!id,
  });

  // Reset organization switching state when fetch completes
  useEffect(() => {
    if (isOrganizationSwitching && !isFetching) {
      setIsOrganizationSwitching(false);
    }
  }, [isOrganizationSwitching, isFetching, setIsOrganizationSwitching]);

  const { mutate: sendInvoiceCreatedEmail, isPending: isSendingEmail } =
    trpc.admin.invoice.adminSendInvoiceEmail.useMutation({
      onSuccess: async () => {
        successToast({
          title: 'Email Sent',
          description: 'Invoice email has been sent successfully.',
        });
        refetch();
      },
      onError: () => {
        errorToast({
          title: 'Email Failed',
          description: 'Failed to send invoice email. Please try again.',
        });
      },
    });

  const { mutate: sendInvoiceSMS, isPending: isSendingSMS } =
    trpc.admin.invoice.adminSendInvoiceSMS.useMutation({
      onSuccess: async () => {
        successToast({
          title: 'SMS Sent',
          description: 'Invoice SMS has been sent successfully.',
        });
        refetch();
      },
      onError: (error) => {
        errorToast({
          title: 'SMS Failed',
          description: error?.message || 'Failed to send invoice SMS. Please try again.',
        });
      },
    });

  const { mutate: setInvoiceAsCanceled } = trpc.admin.invoice.adminCancelInvoice.useMutation({
    onSuccess: async () => {
      successToast({
        title: 'Invoice canceled with success',
        description: 'Invoice email has been sent successfully.',
      });
      refetch();
    },
    onError: () => {
      errorToast({
        title: 'Invoice could not be cancled',
        description: 'Failed to cancel invoice. Please try again.',
      });
    },
  });

  const { mutate: setInvoiceAsDeleted } = trpc.admin.invoice.adminDeleteInvoice.useMutation({
    onSuccess: async () => {
      successToast({
        title: 'Invoice deleted with success',
        description: 'Invoice was deleted.',
      });
      refetch();
    },
    onError: () => {
      errorToast({
        title: 'Invoice could not be deleted',
        description: 'Failed to delete invoice. Please try again.',
      });
    },
  });

  const { mutate: markInvoiceAsSent, isPending: isMarkingAsSent } =
    trpc.admin.invoice.adminMarkInvoiceAsSent.useMutation({
      onSuccess: async () => {
        successToast({
          title: 'Invoice marked as sent',
          description: 'Invoice has been marked as sent successfully.',
        });
        refetch();
      },
      onError: (e) => {
        errorToast({
          title: 'Failed to mark as sent',
          description: e?.message || 'Failed to mark invoice as sent. Please try again.',
        });
      },
    });

  const { mutate: reprocessPayment, isPending: isReprocessingPayment } =
    trpc.admin.invoice.adminReprocessStripePayment.useMutation({
      onSuccess: async (data) => {
        successToast({
          title: 'Payment Reprocessed',
          description: data.message,
        });
        refetch();
      },
      onError: (error) => {
        errorToast({
          title: 'Payment Reprocessing Failed',
          description: error.message || 'Failed to reprocess payment. Please try again.',
        });
      },
    });

  const { mutate: markInvoiceAsPaid, isPending: isMarkingAsPaid } =
    trpc.admin.invoice.adminMarkInvoiceAsPaid.useMutation({
      onSuccess: async () => {
        successToast({
          title: 'Invoice Marked as Paid',
          description: 'Invoice has been marked as paid successfully.',
        });
        refetch();
      },
      onError: (error) => {
        errorToast({
          title: 'Failed to Mark as Paid',
          description: error.message || 'Failed to mark invoice as paid. Please try again.',
        });
      },
    });

  const handleDeleteInvoice = async () => {
    setOpenDeleteDialog(true);
  };

  const handleCancelInvoice = async () => {
    setOpenCancelDialog(true);
  };

  const handleModalClose = () => {
    setOpenSendEmailDialog(false);
    setOpenSendSMSDialog(false);
    setOpenMarkAsSentDialog(false);
  };

  const handleSendEmail = async () => {
    sendInvoiceCreatedEmail(id || '');
  };

  const handleSendEmailWithDialog = () => {
    setOpenSendEmailDialog(true);
  };

  const handleSendSMS = async () => {
    sendInvoiceSMS(id || '');
  };

  const handleSendSMSWithDialog = () => {
    setOpenSendSMSDialog(true);
  };

  const handleMarkAsSent = () => {
    setOpenMarkAsSentDialog(true);
  };

  const confirmSendEmail = () => {
    handleModalClose();
    handleSendEmail();
  };

  const confirmSendSMS = () => {
    handleModalClose();
    handleSendSMS();
  };

  const confirmMarkAsSent = () => {
    handleModalClose();
    markInvoiceAsSent(id || '');
  };

  const confirmDeleteInvoice = () => {
    setInvoiceAsDeleted(id!);
  };

  const confirmCancelInvoice = () => {
    setInvoiceAsCanceled(id!);
  };

  const handleEditInvoice = () => {};

  const handleCopyCustomerLink = async () => {
    await navigator.clipboard.writeText(invoice?.paymentLinkUrl! || '');
    successToast({
      title: 'Customer Link Copied',
      description: 'Customer link has been copied to clipboard.',
    });
  };

  const handleReprocessPayment = () => {
    if (id) {
      reprocessPayment(id);
    }
  };

  const handleMarkAsPaid = () => {
    if (id) {
      markInvoiceAsPaid(id);
    }
  };

  if (!isResolved || isLoading || isOrganizationSwitching) {
    return (
      <MainLayout>
        <Loader title="Loading Invoice Details" />
      </MainLayout>
    );
  }

  if (error || !invoice) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <h2 className="text-xl font-semibold text-red-600">Invoice not found</h2>
          <p className="mt-2 text-gray-600">
            The invoice you're looking for doesn't exist or you don't have permission to view it.
          </p>
          <Button onClick={() => navigate('/invoices')} className="mt-4">
            Back to Invoices
          </Button>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="flex flex-row justify-between items-center">
        <div className="flex flex-col gap-2">
          <h1 className="font-semibold text-xl">Invoice #{invoice.friendlyId}</h1>
          <div className="flex flex-row gap-2 items-center">
            <p className="text-sm text-muted">{formatLongDateTime(invoice.createdAt!)}</p>
            <InvoiceStatusBadge className="text-xs" status={invoice.status} />
          </div>
        </div>

        <div className="flex flex-row gap-2 items-center">
          <Button className="text-text-light" variant={'secondary'}>
            <InvoiceDetailsActionMenu
              isLoading={
                isSendingEmail ||
                isSendingSMS ||
                isMarkingAsSent ||
                isReprocessingPayment ||
                isMarkingAsPaid
              }
              invoiceStatus={invoice.status}
              paymentIntentId={invoice.paymentIntentId}
              onResendInvoice={handleSendEmail}
              onSendInvoice={handleSendEmailWithDialog}
              onSendSMS={handleSendSMSWithDialog}
              onMarkAsSent={handleMarkAsSent}
              onEditInvoice={handleEditInvoice}
              onDeleteInvoice={handleDeleteInvoice}
              onCancelInvoice={handleCancelInvoice}
              onReprocessPayment={handleReprocessPayment}
              onMarkAsPaid={handleMarkAsPaid}
            />
          </Button>
        </div>
      </div>

      <div className="mt-4 flex flex-row gap-10 w-full">
        <div className="flex flex-col gap-4 w-full">
          <h3 className="font-semibold text-lg">Invoice Details</h3>
          <InvoiceCustomerSection
            invoiceId={id!}
            isLoading={isLoading || isFetching}
            userId={invoice.userId}
            refetchInvoice={refetch}
          />
          <InvoiceBillingAddressSection
            invoice={invoice}
            isLoading={isLoading || isFetching}
            refetchInvoice={refetch}
          />
          <InvoiceItemsSection
            invoice={invoice}
            isLoading={isLoading || isFetching}
            refetchInvoice={refetch}
          />
          <InvoiceDetailsSection
            invoice={invoice}
            isLoading={isLoading || isFetching}
            refetchInvoice={refetch}
          />
          <TotalWithTaxFooter
            subtotalInCents={invoice.subtotalInCents || 0}
            taxAmountInCents={invoice.taxAmountInCents || 0}
            totalInCents={invoice.totalInCents || 0}
          />
        </div>

        <div className="flex flex-col gap-8 min-w-[400px] max-w-[650px] w-full">
          <div className="flex flex-col gap-4 w-full">
            <h3 className="font-semibold text-lg">Action</h3>

            <InvoicePaymentLinkSection
              invoice={invoice}
              onCopyCustomerLink={handleCopyCustomerLink}
            />

            <div className="flex flex-col gap-4 w-full">
              <div className="flex gap-4 w-full justify-between items-center">
                <h3 className="font-semibold text-lg">History</h3>
                <Drawer direction="right">
                  <DrawerTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex items-center gap-2 text-text-light hover:text-primary"
                    >
                      <LuHistory className="w-4 h-4" />
                      Email & SMS History
                    </Button>
                  </DrawerTrigger>
                  <DrawerContent className="right-4 left-auto top-4 bottom-4 mt-0 w-[400px] rounded-lg border">
                    <div className="bg-white h-full w-full grow p-5 flex flex-col rounded-lg">
                      <DrawerHeader className="px-0 pt-0">
                        <DrawerTitle className="text-lg font-semibold">
                          Invoice Email & SMS History
                        </DrawerTitle>
                        <DrawerDescription className="text-sm text-muted-foreground">
                          View the complete timeline of emails & SMS for this invoice
                        </DrawerDescription>
                      </DrawerHeader>
                      <div className="flex-1 overflow-y-auto">
                        {invoice.emailHistory.length > 0 && (
                          <div className="space-y-3">
                            {invoice.emailHistory.map((email, index) => (
                              <div
                                key={index}
                                className="flex justify-between items-center p-3 border border-gray-200 rounded"
                              >
                                <div>
                                  <div className="flex items-center gap-2">
                                    <Mail className="h-4 w-4" />
                                    <MessageSquare className="h-4 w-4" />
                                    <span className="font-medium">{email.sentTo}</span>
                                  </div>
                                  <div className="text-sm text-gray-600 mt-1">
                                    {formatLongDateTime(email.sentAt)} • {email.template}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <DrawerFooter className="px-0 pb-0">
                        <DrawerClose asChild>
                          <Button variant="outline" className="w-full">
                            Close
                          </Button>
                        </DrawerClose>
                      </DrawerFooter>
                    </div>
                  </DrawerContent>
                </Drawer>
              </div>
              <div className="space-y-3">
                <div className="px-2 ">
                  <div className="flex flex-col relative gap-6 border-l-2 border-slate-100 pt-6 pl-3">
                    {invoice.statusHistory.map(
                      (
                        status: {
                          status: InvoiceStatus;
                          changedAt: string;
                          changedBy: string;
                          reason?: string;
                        },
                        index: number,
                      ) => (
                        <div
                          className="flex flex-row items-center justify-between"
                          key={`${index} - ${status}`}
                        >
                          <div className="absolute -left-[5px] w-[8px] h-[8px] bg-slate-300 rounded-full"></div>
                          <div className="flex justify-between items-center w-full">
                            <div className="text-xs flex-1 text-slate-500 mr-2">
                              <InvoiceStatusBadge status={status.status} reason={status.reason} />
                            </div>
                            <div className="text-xs text-right text-slate-500">
                              <p>{format(new Date(status.changedAt), "MMM d, yyyy 'at' p")}</p>
                            </div>
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Send Email Confirmation Dialog */}
      <AlertDialog open={openSendEmailDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-left text-text-light text-lg">
              Send Invoice Email
            </AlertDialogTitle>
          </AlertDialogHeader>
          <p className="text-sm text-muted">
            Are you sure you want to send invoice #{invoice?.friendlyId} to the customer?
          </p>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleModalClose}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmSendEmail} disabled={isSendingEmail}>
              {isSendingEmail ? 'Sending...' : 'Send Email'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Send SMS Confirmation Dialog */}
      <AlertDialog open={openSendSMSDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-left text-text-light text-lg">
              Send Invoice SMS
            </AlertDialogTitle>
          </AlertDialogHeader>
          <p className="text-sm text-muted">
            Are you sure you want to send invoice #{invoice?.friendlyId} to the customer via SMS?
          </p>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleModalClose}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmSendSMS} disabled={isSendingSMS}>
              {isSendingSMS ? 'Sending...' : 'Send SMS'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mark as Sent Confirmation Dialog */}
      <AlertDialog open={openMarkAsSentDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-left text-text-light text-lg">
              Mark Invoice as Sent
            </AlertDialogTitle>
          </AlertDialogHeader>
          <p className="text-sm text-muted">
            Are you sure you want to mark invoice #{invoice?.friendlyId} as sent? This will change
            the invoice status to "Sent".
          </p>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleModalClose}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmMarkAsSent} disabled={isMarkingAsSent}>
              {isMarkingAsSent ? 'Marking as Sent...' : 'Mark as Sent'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ConfirmDialog
        open={openDeleteDialog}
        onOpenChange={setOpenDeleteDialog}
        title="Delete Invoice"
        description={`Are you sure you want to delete invoice #${invoice?.friendlyId}? This action cannot be undone.`}
        confirmText="Delete Invoice"
        variant="destructive"
        onConfirm={confirmDeleteInvoice}
      />

      <ConfirmDialog
        open={openCancelDialog}
        onOpenChange={setOpenCancelDialog}
        title="Cancel Invoice"
        description={`Are you sure you want to cancel invoice #${invoice?.friendlyId}? This will change the invoice status to "Cancelled".`}
        confirmText="Cancel Invoice"
        onConfirm={confirmCancelInvoice}
      />
    </MainLayout>
  );
};
